import React from 'react';
import { evaluateResistanceAlerts } from '../utils/microbiologyTemplateEngine';

export const WORKFLOW_STEPS = [
  { id: 'microscopy', stepNo: 1, label: 'Direct Microscopy', shortLabel: 'Microscopy' },
  { id: 'culture', stepNo: 2, label: 'Culture & Incubation', shortLabel: 'Culture' },
  { id: 'ast', stepNo: 3, label: 'Organism ID & AST', shortLabel: 'Organisms & AST' },
  { id: 'resistance', stepNo: 4, label: 'Resistance & Clinical Rules', shortLabel: 'Resistance' },
  { id: 'comments', stepNo: 5, label: 'Interpretive Remarks', shortLabel: 'Remarks' },
  { id: 'history', stepNo: 6, label: 'Patient History', shortLabel: 'History' },
  { id: 'validation', stepNo: 7, label: 'Review & Release', shortLabel: 'Review & Release' }
];

export default function WorkflowProgressBar({ data, activeTab, onSelectTab }) {
  if (!data) return null;

  // Determine stage completion states
  const hasMicroscopy = Boolean(data.gramStain || data.pusCells || data.microscopyObservations);
  const hasCulture = Boolean(data.cultureStatus && data.cultureStatus !== 'Pending');
  const isNoGrowth = data.cultureStatus === 'No Growth';

  const isolateCount = data.isolates?.length || 0;
  const hasIsolates = isolateCount > 0;

  const allAst = (data.isolates || []).flatMap((i) => i.astResults || []);
  const measuredAst = allAst.filter((r) => r.measuredValue !== null && r.measuredValue !== undefined);
  const hasAst = measuredAst.length > 0;

  const resistanceAlerts = (data.isolates || []).flatMap((i) => evaluateResistanceAlerts(i));
  const hasResistanceAlerts = resistanceAlerts.length > 0;

  const hasComments = Boolean(data.finalRemarks);
  const isApproved = Boolean(data.approved);

  const getStepStatus = (stepId) => {
    switch (stepId) {
      case 'microscopy':
        return hasMicroscopy ? 'completed' : 'in-progress';
      case 'culture':
        return hasCulture ? 'completed' : 'in-progress';
      case 'ast':
        if (isNoGrowth) return 'skipped';
        if (hasIsolates && hasAst) return 'completed';
        if (hasIsolates) return 'in-progress';
        return hasCulture ? 'pending' : 'locked';
      case 'resistance':
        if (isNoGrowth) return 'skipped';
        if (hasResistanceAlerts) return 'alert';
        if (hasIsolates) return 'completed';
        return 'pending';
      case 'comments':
        return hasComments ? 'completed' : 'pending';
      case 'history':
        return data.previousHistory?.length > 0 ? 'completed' : 'neutral';
      case 'validation':
        return isApproved ? 'completed' : !hasCulture ? 'locked' : 'ready';
      default:
        return 'pending';
    }
  };

  return (
    <nav className="wb-workflow-navigator" aria-label="Microbiology Workflow Stages">
      <div className="wb-workflow-track">
        {WORKFLOW_STEPS.map((step, idx) => {
          const isCurrent = activeTab === step.id;
          const status = getStepStatus(step.id);

          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                className={`wb-workflow-step-btn ${isCurrent ? 'active' : ''} ${status}`}
                onClick={() => onSelectTab(step.id)}
                title={`Step ${step.stepNo}: ${step.label} (${status})`}
              >
                <div className="wb-step-status-icon">
                  {status === 'completed' && !isCurrent ? (
                    <span>✓</span>
                  ) : status === 'locked' && !isCurrent ? (
                    <span>🔒</span>
                  ) : status === 'alert' && !isCurrent ? (
                    <span>!</span>
                  ) : (
                    <span>{step.stepNo}</span>
                  )}
                </div>

                <div className="wb-step-text-wrap">
                  <span className="wb-step-seq-label">Step {step.stepNo}</span>
                  <strong className="wb-step-name-text">{step.shortLabel}</strong>
                </div>
              </button>

              {idx < WORKFLOW_STEPS.length - 1 && (
                <div className={`wb-step-link-line ${status === 'completed' ? 'done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
}
